<template>
	<main>
		<HeaderTab :title="$t('global.cards_dashboard')"/>
		<div id="content">
			<div class="container-fluid">
				<!-- Sélection / affichage -->
				<div class="box">
					<div class="row align-items-center mb-3">
						<div class="col-12 col-md-10 mb-2 mb-md-0">
							<LoadingSpinner v-if="loading" />
							<template v-else>
								<e-select
									v-model="selectedDashboard"
									:options="dashboards"
									track-by="dashboards_id"
									label="dashboards_name"
									:placeholder="$t('global.rechercher')"
								/>
								<b-alert show variant="warning" class="my-2" v-if="!hasDefaultDashboard && !selectedDashboard">
									{{ $t('cards.dashboard.no_default_dashboard') }}
								</b-alert>
							</template>
						</div>
						<div class="col-12 col-md-2">
							<b-button variant="primary" block @click="setupDashboard()">
								<font-awesome-icon :icon="['fal', 'plus']" /> {{ $t('global.ajouter') }}
							</b-button>
						</div>
					</div>

					<div v-if="selectedDashboard">
						<LoadingSpinner v-if="loadingWidgets" class="col-12" />
						<template v-else-if="!visibleDashboardWidgets.length">
							<span class="text-muted text-center">{{ $t('cards.dashboard.no_widget') }}</span>
						</template>
						<div v-else class="row">
							<div v-for="w in visibleDashboardWidgets" :key="w.dashboardwidgets_id" :class="widgetColClass(w)">
								<component :is="componentName(w.widgettype)" :widget="w" />
							</div>
						</div>

						<div class="text-center mt-3">
							<b-button pill variant="primary" @click="openDashboardModal">
								{{ $t('global.modifier') }}
							</b-button>
							<b-button pill variant="secondary" class="ml-2" @click="confirmDeleteDashboard">
								{{ $t('global.supprimer') }}
							</b-button>
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal suppression dashboard -->
		<ModalConfirm
			v-if="selectedDashboard"
			ref="modalDeleteDashboard"
			:icon="['fal', 'trash-alt']"
			:text_title="$t('cards.dashboard.confirm_delete_title')"
			:text_question="$t('cards.dashboard.confirm_delete_msg', { name: selectedDashboard.dashboards_name })"
			:text_button_ok="$t('global.supprimer')"
			:text_button_cancel="$t('global.annuler')"
			:callback_validation="doDeleteDashboard"
		/>

		<!-- Modal créer / modifier dashboard -->
		<b-modal v-model="showDashboardModal" hide-footer size="xl">
			<template v-slot:modal-title>
				{{ selectedDashboard ? $t('cards.dashboard.edit_dashboard') : $t('cards.dashboard.new_dashboard') }}
			</template>

			<!-- Informations -->
			<b-form-group :label="$t('cards.dashboard.name')">
				<b-form-input v-model="modalName" />
			</b-form-group>

			<div class="mb-3">
				<b-form-checkbox v-model="isDefault">{{ $t('cards.dashboard.default_dashboard') }}</b-form-checkbox>
			</div>

			<div v-if="hasAdminAccess" class="mb-3">
				<b-form-checkbox
					switch
					v-model="modalShared"
					@change="onToggleShared"
				>
					<font-awesome-icon :icon="modalShared ? ['fal', 'users'] : ['fal', 'times-circle']" class="mr-1" />
					{{ modalShared ? $t('cards.dashboard.shared') : $t('cards.dashboard.private') }}
				</b-form-checkbox>

				<div v-if="modalShared" class="mt-2 border rounded p-2">
					<div v-if="loadingLicenceUsers" class="text-center py-2">
						<font-awesome-icon :icon="['fas', 'spinner']" pulse />
					</div>
					<div v-else>
						<div
							v-for="u in licenceUsers"
							:key="u.email"
							class="py-1"
						>
							<label class="d-flex align-items-center mb-0" style="cursor: pointer">
								<b-form-checkbox
									v-model="sharedWithUsers"
									:value="u.id"
									class="mr-2"
								/>
								<span>{{ u.firstname }} {{ u.lastname }} <span class="text-muted small">({{ u.email }})</span></span>
							</label>
							<b-alert
								v-if="getUserMissingModules(u).length"
								show
								variant="warning"
								class="py-1 px-2 mt-1 mb-0 small"
							>
								<font-awesome-icon :icon="['fal', 'exclamation-triangle']" class="mr-1" />
								{{ $t('cards.dashboard.missing_modules') }} : {{ getUserMissingModules(u).join(', ') }}
							</b-alert>
						</div>
					</div>
				</div>
			</div>

			<hr />

			<!-- Widgets -->
			<div class="d-flex align-items-center justify-content-between mb-2">
				<h5 class="mb-0">{{ $t('cards.dashboard.widgets') }}</h5>
				<b-button pill variant="secondary" size="sm" @click="addWidgetToEdit">
					<font-awesome-icon :icon="['fal', 'plus']" /> {{ $t('cards.dashboard.add_widget') }}
				</b-button>
			</div>

			<div v-if="loadingEditParams" class="text-center py-3">
				<font-awesome-icon :icon="['fas', 'spinner']" pulse />
			</div>
			<template v-else>
				<span v-if="!editWidgets.length" class="text-muted text-center">{{ $t('cards.dashboard.no_widget') }}</span>
				<Draggable v-else class="list-group mb-3" :list="editWidgets" v-bind="dragOptions" group="widgets">
					<div
						v-for="w in editWidgets"
						:key="w.isNew ? w.tempId : w.dashboardwidgets_id"
						:ref="'editWidget_' + (w.isNew ? w.tempId : w.dashboardwidgets_id)"
						class="list-group-item"
						:class="{ 'border-left border-primary': w.isNew }"
					>
						<!-- Header : drag + infos de base + supprimer -->
						<div class="d-flex align-items-center">
							<font-awesome-icon :icon="['fal', 'sort-alt']" class="drag-handle text-muted mr-3 flex-shrink-0" style="cursor: grab" />
							<div class="d-flex flex-grow-1 align-items-center mr-2" style="gap: 12px;">
								<b-form-group v-if="w.isNew" :label="$t('cards.dashboard.widget_type') + ' *'" class="mb-0" style="min-width: 350px;">
									<e-select
										:value="widgetTypes.find(t => t.widgettypes_id === w.dashboardwidget_widgettype)"
										:options="widgetTypes"
										track-by="widgettypes_id"
										:custom-label="widgetTypeLabel"
										:placeholder="$t('global.rechercher')"
										@input="onNewWidgetTypeChange(w, $event)"
									/>
								</b-form-group>
								<span v-else class="text-muted small flex-shrink-0">{{ widgetTypeLabel(w.widgettype) }}</span>
								<template v-if="!w.isNew || w.dashboardwidget_widgettype">
									<b-form-group :label="$t('cards.dashboard.widget_title') + ' *'" class="mb-0 flex-grow-1">
										<b-form-input v-model="w.dashboardwidgets_title" size="sm" :placeholder="$t('cards.dashboard.widget_title')" />
									</b-form-group>
									<b-form-group :label="$t('cards.dashboard.widget_size') + ' *'" class="mb-0" style="width: 80px;">
										<b-form-input type="number" v-model.number="w.dashboardwidgets_width" size="sm" min="1" max="4" />
									</b-form-group>
								</template>
							</div>
							<b-button variant="secondary" size="sm" class="flex-shrink-0" @click="removeWidget(w)">
								<font-awesome-icon :icon="['fal', 'trash-alt']" />
							</b-button>
						</div>

						<!-- Params dynamiques -->
						<div v-if="w.dashboardwidget_widgettype && getWidgetTypeParams(w.dashboardwidget_widgettype).length" class="d-flex flex-wrap mt-2" style="gap: 12px; padding-left: 28px;">
							<b-form-group
								v-for="param in getWidgetTypeParams(w.dashboardwidget_widgettype)"
								:key="param.name"
								:label="paramLabel(param, w.dashboardwidgets_config)"
								class="mb-0"
								style="min-width: 180px; flex: 1;"
							>
								<e-select
									v-if="param.type === 'select'"
									v-model="w.dashboardwidgets_config[param.name]"
									:options="editWidgetParamOptions[param.name] || []"
									track-by="code"
									label="label"
									size="sm"
								/>
								<b-form-input
									v-else-if="param.type === 'number'"
									type="number"
									v-model.number="w.dashboardwidgets_config[param.name]"
									size="sm"
								/>
							</b-form-group>
						</div>
					</div>
				</Draggable>
			</template>

			<div class="text-center mt-3">
				<b-button pill variant="primary" :disabled="!canSaveModal" @click="saveModal">
					{{ $t('global.enregistrer') }}
				</b-button>
			</div>

			<ActionOverlay
				ref="saveOverlay"
				:loadingText="$t('cards.dashboard.saving')"
				:successText="$t('cards.dashboard.save_success')"
			/>
		</b-modal>
	</main>
</template>

<script type="text/javascript">
import DashboardMixin from "@/mixins/Dashboard.js"
import UserMixin from "@/mixins/User.js"
import draggable from "vuedraggable"
import Permissions from "@/mixins/Permissions.js"

export default {
	name: 'Dashboard',
	mixins: [DashboardMixin, UserMixin, Permissions],
	data () {
		return {
			loading: true,
			loadingWidgets: false,
			isDefault: false,
			dashboards: [],
			selectedDashboard: null,
			currentWidgets: [],
			visibleDashboardWidgets: [],
			widgetTypes: [],
			showDashboardModal: false,
			modalName: '',
			modalShared: false,
			loadingSaveInfo: false,
			editWidgetParamOptions: {},
			editWidgets: [],
			loadingEditParams: false,
			licenceUsers: [],
			loadingLicenceUsers: false,
			sharedWithUsers: [],
			currentUserRights: null,
			dragOptions: { animation: 200, handle: '.drag-handle' },
		}
	},

	mounted() {
		this.init_component()
	},

	methods: {
		async init_component() {
			this.currentUserRights = this.getUserAccessRights()
			const dashboards = await this.getDashboards()
			this.dashboards = dashboards
			this.selectedDashboard = dashboards.find(d => d.dashboards_is_default)
			this.loading = false
		},
		componentName(widgetType) {
			return widgetType.widgettypes_key.split('_').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('')
		},
		widgetColClass(w) {
			// Largeur configurée (1 à 4) → colonnes Bootstrap sur grand écran (3, 6, 9, 12).
			// Responsive : pleine largeur sur téléphone, par 2 sur tablette (sauf les grands
			// widgets en pleine largeur), largeur configurée sur bureau.
			const width = w.dashboardwidgets_width || 4
			const lg = width * 3
			const md = width >= 3 ? 12 : 6
			return `col-12 col-md-${md} col-lg-${lg} mb-3`
		},
		async setupDashboard() {
			if (!this.widgetTypes.length) {
				this.widgetTypes = await this.getWidgetTypes()
			}
			this.selectedDashboard = null
			this.modalName = ''
			this.modalShared = false
			this.isDefault = false
			this.sharedWithUsers = []
			this.editWidgets = []
			this.showDashboardModal = true
		},
		async openDashboardModal() {
			if (!this.widgetTypes.length) {
				this.widgetTypes = await this.getWidgetTypes()
			}
			this.modalName = this.selectedDashboard.dashboards_name
			this.modalShared = !!this.selectedDashboard.dashboards_shared
			this.isDefault = !!this.selectedDashboard.dashboards_is_default
			this.sharedWithUsers = this.selectedDashboard.dashboards_shared_users ?? []
			this.editWidgets = this.currentWidgets.map(w => ({
				...w,
				dashboardwidgets_config: w.dashboardwidgets_config || {},
			}))
			this.showDashboardModal = true
			if (this.modalShared && !this.licenceUsers.length) {
				this.loadLicenceUsers()
			}
			this.loadingEditParams = true
			await this.loadEditWidgetParamOptions()
			this.loadingEditParams = false
		},
		async onToggleShared(val) {
			if (val && !this.licenceUsers.length) {
				await this.loadLicenceUsers()
			}
		},
		async loadLicenceUsers() {
			this.loadingLicenceUsers = true
			const licenceUsers = await this.getUserLicence()
			const managedUsers = await this.getUsersToManage()
			this.licenceUsers = licenceUsers.map(u => {
				const managed = managedUsers.retour.find(m => m.email === u.email)
				return {...u, actual_rights: managed.actual_rights, is_admin: !!managed.is_admin}
			})
			this.loadingLicenceUsers = false
		},
		getUserMissingModules(user) {
			if (user.is_admin) return []
			return this.editWidgets
				.map(w => w.widgettype && w.widgettype.widgettypes_rights)
				.filter((rights, index, self) => rights && self.indexOf(rights) === index && !user.actual_rights.includes(rights))
		},
		async saveModal() {
			this.loadingSaveInfo = true
			this.$refs.saveOverlay.show()

			if (!this.selectedDashboard) {
				const widgets = this.editWidgets.map((w, i) => ({
					dashboardwidget_widgettype: w.dashboardwidget_widgettype,
					dashboardwidgets_title: w.dashboardwidgets_title,
					dashboardwidgets_width: w.dashboardwidgets_width,
					dashboardwidgets_order: i + 1,
					dashboardwidgets_config: this.configToStore(this.getWidgetTypeParams(w.dashboardwidget_widgettype), w.dashboardwidgets_config),
				}))
				const created = await this.createDashboard(this.modalName, this.isDefault, this.modalShared ? this.sharedWithUsers : [], widgets)
				this.dashboards = await this.getDashboards()
				this.selectedDashboard = this.dashboards.find(d => d.dashboards_id === created.retour.dashboards_id)
			} else {
				const dashboardId = this.selectedDashboard.dashboards_id
				const widgets = this.editWidgets.map((w, i) => ({
					dashboardwidgets_id: w.isNew ? null : w.dashboardwidgets_id,
					dashboardwidget_widgettype: w.dashboardwidget_widgettype,
					dashboardwidgets_title: w.dashboardwidgets_title,
					dashboardwidgets_width: w.dashboardwidgets_width,
					dashboardwidgets_order: i + 1,
					dashboardwidgets_config: this.configToStore(this.getWidgetTypeParams(w.dashboardwidget_widgettype), w.dashboardwidgets_config),
				}))

				await this.updateDashboard(dashboardId, {
					dashboards_name: this.modalName,
					dashboards_shared: this.modalShared,
					dashboards_shared_users: this.modalShared ? this.sharedWithUsers : [],
					dashboards_is_default: this.isDefault,
					widgets,
				})

				this.dashboards = await this.getDashboards()
				this.selectedDashboard = this.dashboards.find(d => d.dashboards_id === dashboardId)
			}

			this.loadingSaveInfo = false
			await this.$refs.saveOverlay.showSuccess()
			this.showDashboardModal = false
		},
		confirmDeleteDashboard() {
			this.$refs.modalDeleteDashboard.openModal()
		},
		async doDeleteDashboard() {
			this.$refs.modalDeleteDashboard.closeModal()
			await this.deleteDashboard(this.selectedDashboard.dashboards_id)
			this.dashboards = await this.getDashboards()
			this.selectedDashboard = null
		},
		async loadCurrentWidgets(dashboard) {
			if (!dashboard) {
				this.currentWidgets = []
				this.visibleDashboardWidgets = []
				return
			}
			this.loadingWidgets = true
			this.currentWidgets = dashboard.widgets
			const rights = this.currentUserRights.split('-')
			this.visibleDashboardWidgets = this.currentWidgets.filter(w => {
				if (!w.widgettype) return false
				if (!w.widgettype.widgettypes_rights) return true
				return rights.indexOf(w.widgettype.widgettypes_rights) !== -1
			})
			this.loadingWidgets = false
		},
		configToStore(params, config) {
			const result = {}
			for (const param of params) {
				const val = config[param.name]
				result[param.name] = (param.type === 'select' && val && typeof val === 'object') ? val.code : val
			}
			return result
		},
		configFromStore(params, config, options) {
			const result = {}
			for (const param of params) {
				const val = config[param.name]
				if (param.type === 'select' && val !== null && val !== undefined) {
					result[param.name] = (options[param.name] || []).find(o => o.code === val) ?? val
				} else {
					result[param.name] = val ?? null
				}
			}
			return result
		},
		getWidgetTypeParams(widgettype_id) {
			const wt = this.widgetTypes.find(w => w.widgettypes_id === widgettype_id)
			return wt && wt.widgettypes_params ? wt.widgettypes_params : []
		},
		async loadEditWidgetParamOptions() {
			this.editWidgetParamOptions = {}
			const allParams = this.editWidgets.flatMap(w => this.getWidgetTypeParams(w.dashboardwidget_widgettype))
			for (const param of allParams) {
				if (param.type !== 'select' || this.editWidgetParamOptions[param.name]) continue
				if (typeof param.data === 'string') {
					this.$set(this.editWidgetParamOptions, param.name, await this[param.data]())
				} else if (Array.isArray(param.data)) {
					this.$set(this.editWidgetParamOptions, param.name, param.data.map(opt => ({ ...opt, label: this.$t(opt.label) })))
				}
			}
			for (const w of this.editWidgets) {
				const params = this.getWidgetTypeParams(w.dashboardwidget_widgettype)
				if (params.length) {
					this.$set(w, 'dashboardwidgets_config', this.configFromStore(params, w.dashboardwidgets_config, this.editWidgetParamOptions))
				}
			}
		},
		currencySymbolFromConfig(config) {
			const currency = config.entite && config.entite.currency
			if (!currency) return ''
			const parts = new Intl.NumberFormat('fr-FR', { style: 'currency', currency }).formatToParts(0)
			const part = parts.find(p => p.type === 'currency')
			return part ? part.value : currency
		},
		paramLabel(param, config) {
			const base = param.label ? this.$t(param.label) : param.name
			const required = param.optional ? '' : ' *'
			const currency = param.name === 'objectif_ca' && config.entite ? ` (${this.currencySymbolFromConfig(config)})` : ''
			return base + required + currency
		},
		removeWidget(widget) {
			this.editWidgets = this.editWidgets.filter(w =>
				widget.isNew ? w.tempId !== widget.tempId : w.dashboardwidgets_id !== widget.dashboardwidgets_id
			)
		},
		async addWidgetToEdit() {
			const tempId = Date.now()
			this.editWidgets.push({
				isNew: true,
				tempId,
				dashboardwidget_widgettype: null,
				dashboardwidgets_title: '',
				dashboardwidgets_width: 4,
				dashboardwidgets_config: {},
			})
			// Fait défiler jusqu'au widget qu'on vient d'ajouter
			await this.$nextTick()
			const ref = this.$refs['editWidget_' + tempId]
			const node = Array.isArray(ref) ? ref[0] : ref
			if (node && node.scrollIntoView) {
				node.scrollIntoView({ behavior: 'smooth', block: 'center' })
			}
		},
		async onNewWidgetTypeChange(w, widgettype) {
			const params = widgettype.widgettypes_params
			const newConfig = {}
			for (const param of params) {
				newConfig[param.name] = param.default ?? null
				if (param.type !== 'select' || this.editWidgetParamOptions[param.name]) continue
				if (typeof param.data === 'string') {
					this.$set(this.editWidgetParamOptions, param.name, await this[param.data]())
				} else if (Array.isArray(param.data)) {
					const options = param.data.map(opt => ({ ...opt, label: this.$t(opt.label) }))
					this.$set(this.editWidgetParamOptions, param.name, options)
					if (param.default !== undefined) {
						newConfig[param.name] = options.find(o => o.code === param.default) ?? null
					}
				}
			}
			w.dashboardwidget_widgettype = widgettype.widgettypes_id
			w.widgettype = widgettype
			this.$set(w, 'dashboardwidgets_config', newConfig)
		},
	},

	computed: {
		widgetTypeLabel() {
			return wt => this.$t('cards.widget.' + wt.widgettypes_key)
		},
		canSaveModal() {
			if (!this.modalName || this.loadingSaveInfo) return false
			return this.editWidgets.every(w => {
				if (!w.dashboardwidget_widgettype || !w.dashboardwidgets_title || !w.dashboardwidgets_width) return false
				const params = this.getWidgetTypeParams(w.dashboardwidget_widgettype)
				return params.every(param => {
					if (param.optional) return true
					const val = w.dashboardwidgets_config[param.name]
					return val !== null && val !== undefined && val !== ''
				})
			})
		},
		hasDefaultDashboard() {
			return this.dashboards.some(d => d.dashboards_is_default)
		},
		hasAdminAccess() {
			return this.$store.state.userAccess.hasAdminAccess
		},
	},

	watch: {
		selectedDashboard(val) {
			if (!val) return
			this.loadCurrentWidgets(val)
		},
	},

	components: {
		HeaderTab:       () => import('@/components/HeaderTab'),
		LoadingSpinner:  () => import('GroomyRoot/components/Logos/LoadingSpinner_35'),
		ModalConfirm:    () => import('@/components/Modals/ModalConfirm'),
		ActionOverlay:   () => import('@/components/Utils/ActionOverlay'),
		Draggable:       draggable,
		InvoiceUnpaid:   () => import('@/components/WidgetUnpaid'),
		GraphiqueCa:     () => import('@/components/WidgetCA'),
		ContractMap:     () => import('@/components/WidgetContractMap'),
		CategoryHorse:	 () => import('@/components/WidgetCategoryHorse'),
		ActesEnRetard:	 () => import('@/components/WidgetUndoneActs'),
		EtatPhysiologique: () => import('@/components/WidgetPhysiologicalStates'),
	}
}

</script>
